import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { recordAudit } from '@/lib/audit-client';
import {
  Client,
  Demo,
  CloserRemovalRequest,
  PipelineItem,
  SupportTicket,
  UpgradeRequest,
  TeamMember,
} from '@/types/client';

// ---------------------------------------------------------------------------
// Supabase row -> app-type mappers
// ---------------------------------------------------------------------------

function mapClientRow(row: any): Client {
  const creds = row.credentials as any;
  const services = row.services_enabled as any;

  return {
    id: row.id,
    name: row.company_name ?? '',
    contactName: row.contact_name ?? '',
    email: row.email ?? '',
    package: row.package === 'elite' ? 'elite' : row.package === 'test' ? 'test' : 'standard',
    services: {
      demos: services?.demos ?? false,
    },
    integrations: {
      ghl: false,
      metaAds: false,
      googleAds: false,
    },
    status: row.status === 'churned' ? 'churned' : 'active',
    joinedDate: row.created_at ?? new Date().toISOString(),
    lastContact: row.updated_at ?? row.created_at ?? new Date().toISOString(),
    credentials: creds
      ? {
          username: creds.username ?? '',
          password: creds.initialPassword ?? creds.password ?? '',
          clientCode: creds.clientCode ?? '',
        }
      : undefined,
    paymentMethod: undefined, // payment_methods is a separate table; not merged here
    fanbasisPaymentLink: row.fanbasis_payment_link ?? null,
    fanbasisCheckoutSessionId: row.fanbasis_checkout_session_id ?? null,
  };
}

function mapDemoRow(row: any): Demo {
  return {
    id: row.id,
    clientId: row.client_id,
    clientName: undefined, // populated separately when needed
    companyName: row.prospect_name ?? '',
    contactName: row.prospect_name ?? '',
    contactTitle: '',
    dateTime: row.demo_date + (row.demo_time ? `T${row.demo_time}` : ''),
    closer: row.closers?.name ?? '',
    closerId: row.closer_id ?? undefined,
    duration: 30, // default; not stored in DB
    status: (['completed', 'scheduled', 'no_show', 'cancelled'].includes(row.status)
      ? row.status
      : 'scheduled') as Demo['status'],
    outcome: (['closed', 'lost', 'pending'].includes(row.outcome)
      ? row.outcome
      : undefined) as Demo['outcome'] | undefined,
    dealValue: row.deal_value ?? undefined,
    dealClosedDate: undefined,
    recordingUrl: undefined,
    source: (['meta', 'google', 'linkedin'].includes(row.source)
      ? row.source
      : 'meta') as Demo['source'],
  };
}

function mapRemovalRequestRow(row: any): CloserRemovalRequest {
  return {
    id: row.id,
    clientId: row.client_id,
    clientName: row.clients?.company_name ?? '',
    closerId: row.closer_id ?? '',
    closerName: row.closers?.name ?? '',
    reason: row.reason ?? undefined,
    requestType: row.request_type === 'replacement' ? 'replacement' : 'removal',
    timestamp: row.created_at ?? new Date().toISOString(),
    status: (['pending', 'approved', 'dismissed'].includes(row.status)
      ? row.status
      : 'pending') as CloserRemovalRequest['status'],
  };
}

function mapPipelineRow(row: any): PipelineItem {
  const stageMap: Record<string, PipelineItem['status']> = {
    lead: 'pre-onboard',
    demo_booked: 'pre-onboard',
    demo_showed: 'active',
    closed: 'closed',
  };

  return {
    id: row.id,
    clientName: row.closer_name ?? row.setter_name ?? '',
    status: stageMap[row.stage] ?? 'pre-onboard',
    value: row.deal_value_cents ? row.deal_value_cents / 100 : 0,
    lastContact: row.updated_at ?? row.created_at ?? new Date().toISOString(),
    nextStep: row.stage ?? '',
  };
}

function mapTeamMemberRow(row: any): TeamMember {
  const roleMap: Record<string, TeamMember['role']> = {
    super_admin: 'super_admin',
    team_admin: 'account_manager',
    member: 'operations',
  };

  return {
    id: row.id,
    name: row.full_name ?? '',
    email: row.email ?? '',
    role: roleMap[row.role] ?? 'custom',
    permissions: [],
    status: row.status === 'inactive' ? 'inactive' : 'active',
    createdAt: row.created_at ?? new Date().toISOString(),
    credentials: undefined,
  };
}

// ---------------------------------------------------------------------------
// App-type -> Supabase insert mappers
// ---------------------------------------------------------------------------

function clientToRow(client: Omit<Client, 'id'>) {
  return {
    company_name: client.name,
    contact_name: client.contactName,
    email: client.email,
    package: client.package,
    services_enabled: client.services,
    status: client.status ?? 'active',
    credentials: client.credentials ?? null,
  };
}

function demoToRow(demo: Omit<Demo, 'id'>) {
  const [datePart, timePart] = (demo.dateTime ?? '').split('T');
  return {
    client_id: demo.clientId,
    prospect_name: demo.contactName || demo.companyName,
    prospect_email: null,
    demo_date: datePart || new Date().toISOString().slice(0, 10),
    demo_time: timePart || null,
    closer_id: demo.closerId || null,
    status: demo.status ?? 'scheduled',
    outcome: demo.outcome ?? null,
    deal_value: demo.dealValue ?? null,
    source: demo.source ?? null,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useClients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [demos, setDemos] = useState<Demo[]>([]);
  const [removalRequests, setRemovalRequests] = useState<CloserRemovalRequest[]>([]);
  const [pipeline, setPipeline] = useState<PipelineItem[]>([]);
  const [tickets] = useState<SupportTicket[]>([]);
  const [upgradeRequests] = useState<UpgradeRequest[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  // ----- helpers to refetch individual collections -----

  const refreshClients = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('company_name');
      if (error) {
        console.error('[useClients] refreshClients error:', error);
        return;
      }
      if (data) setClients(data.map(mapClientRow));
    } catch (err) {
      console.error('[useClients] refreshClients exception:', err);
    }
  }, []);

  const refreshDemos = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('demos')
        .select('*, closers(name)')
        .order('demo_date', { ascending: false });
      if (error) {
        console.error('[useClients] refreshDemos error:', error);
        return;
      }
      if (data) setDemos(data.map(mapDemoRow));
    } catch (err) {
      console.error('[useClients] refreshDemos exception:', err);
    }
  }, []);

  const refreshRemovalRequests = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('closer_requests')
        .select('*, clients(company_name), closers(name)');
      if (error) {
        console.error('[useClients] refreshRemovalRequests error:', error);
        return;
      }
      if (data) setRemovalRequests(data.map(mapRemovalRequestRow));
    } catch (err) {
      console.error('[useClients] refreshRemovalRequests exception:', err);
    }
  }, []);

  const refreshPipeline = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('funnel_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) {
        console.error('[useClients] refreshPipeline error:', error);
        return;
      }
      if (data) setPipeline(data.map(mapPipelineRow));
    } catch (err) {
      console.error('[useClients] refreshPipeline exception:', err);
    }
  }, []);

  const refreshTeamMembers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at');
      if (error) {
        console.error('[useClients] refreshTeamMembers error:', error);
        return;
      }
      if (data) setTeamMembers(data.map(mapTeamMemberRow));
    } catch (err) {
      console.error('[useClients] refreshTeamMembers exception:', err);
    }
  }, []);

  // ----- initial fetch -----

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([
        refreshClients(),
        refreshDemos(),
        refreshRemovalRequests(),
        refreshPipeline(),
        refreshTeamMembers(),
      ]);
      setLoading(false);
    };

    fetchAll();
  }, [refreshClients, refreshDemos, refreshRemovalRequests, refreshPipeline, refreshTeamMembers]);

  // ----- Client CRUD -----

  const addClient = useCallback(
    async (client: Omit<Client, 'id'>) => {
      try {
        const { data, error } = await supabase
          .from('clients')
          .insert(clientToRow(client))
          .select()
          .single();

        if (error) {
          console.error('[useClients] addClient error:', error);
          // Return a stub so callers that depend on a return value don't break
          const stub: Client = { ...client, id: crypto.randomUUID() };
          return stub;
        }

        const mapped = mapClientRow(data);
        setClients((prev) => [...prev, mapped]);
        return mapped;
      } catch (err) {
        console.error('[useClients] addClient exception:', err);
        const stub: Client = { ...client, id: crypto.randomUUID() };
        return stub;
      }
    },
    [],
  );

  const updateClient = useCallback(
    async (id: string, updates: Partial<Client>) => {
      try {
        const row: Record<string, any> = {};
        if (updates.name !== undefined) row.company_name = updates.name;
        if (updates.contactName !== undefined) row.contact_name = updates.contactName;
        if (updates.email !== undefined) row.email = updates.email;
        if (updates.package !== undefined) row.package = updates.package;
        if (updates.services !== undefined) row.services_enabled = updates.services;
        if (updates.status !== undefined) row.status = updates.status;
        if (updates.credentials !== undefined) row.credentials = updates.credentials;

        const { error } = await supabase.from('clients').update(row).eq('id', id);

        if (error) {
          console.error('[useClients] updateClient error:', error);
        }

        // Optimistically update local state regardless
        setClients((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
      } catch (err) {
        console.error('[useClients] updateClient exception:', err);
        setClients((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
      }
    },
    [],
  );

  // Pause client: temporarily suspend access, preserves all data
  const pauseClient = useCallback(
    async (id: string) => {
      try {
        const { error } = await supabase
          .from('clients')
          .update({ status: 'paused' })
          .eq('id', id);
        if (error) console.error('[useClients] pauseClient error:', error);
        else await recordAudit('client.status_change', { table: 'clients', id }, { to: 'paused' });
        setClients((prev) => prev.map((c) => c.id === id ? { ...c, status: 'paused' } : c));
      } catch (err) {
        console.error('[useClients] pauseClient exception:', err);
      }
    },
    [],
  );

  // Unpause client: restore active status
  const unpauseClient = useCallback(
    async (id: string) => {
      try {
        const { error } = await supabase
          .from('clients')
          .update({ status: 'active' })
          .eq('id', id);
        if (error) console.error('[useClients] unpauseClient error:', error);
        else await recordAudit('client.status_change', { table: 'clients', id }, { to: 'active' });
        setClients((prev) => prev.map((c) => c.id === id ? { ...c, status: 'active' } : c));
      } catch (err) {
        console.error('[useClients] unpauseClient exception:', err);
      }
    },
    [],
  );

  // Deactivate client: soft delete, preserves all data for records
  const deactivateClient = useCallback(
    async (id: string) => {
      try {
        const { error } = await supabase
          .from('clients')
          .update({ status: 'inactive' })
          .eq('id', id);
        if (error) console.error('[useClients] deactivateClient error:', error);
        else await recordAudit('client.status_change', { table: 'clients', id }, { to: 'inactive' });
        setClients((prev) => prev.filter((c) => c.id !== id));
      } catch (err) {
        console.error('[useClients] deactivateClient exception:', err);
        setClients((prev) => prev.filter((c) => c.id !== id));
      }
    },
    [],
  );

  // Hard delete: permanently remove client, auth user, and all associated data (super_admin only)
  const deleteClient = useCallback(
    async (id: string) => {
      // Call edge function to delete auth user + client row (service role required)
      const { data, error: fnError } = await supabase.functions.invoke('delete-client-user', {
        body: { client_id: id },
      });

      // Check if the delete actually succeeded — the function returns { success: true }
      // The Supabase relay may report an error even when the function succeeds
      if (fnError && !(data as any)?.success) {
        // Verify the client is actually gone before throwing
        const { data: check } = await supabase.from('clients').select('id').eq('id', id).maybeSingle();
        if (check) {
          // Client still exists — real failure
          console.error('[useClients] deleteClient edge function error:', fnError);
          throw new Error(fnError.message || 'Failed to delete client');
        }
        // Client is gone — delete succeeded despite the error response
      }

      // Update local state
      setClients((prev) => prev.filter((c) => c.id !== id));
      setDemos((prev) => prev.filter((d) => d.clientId !== id));
    },
    [],
  );

  // ----- Demo CRUD -----

  const addDemo = useCallback(
    async (demo: Omit<Demo, 'id'>) => {
      try {
        const { data, error } = await supabase
          .from('demos')
          .insert(demoToRow(demo))
          .select('*, closers(name)')
          .single();

        if (error) {
          console.error('[useClients] addDemo error:', error);
          const stub: Demo = { ...demo, id: crypto.randomUUID() };
          return stub;
        }

        const mapped = mapDemoRow(data);
        setDemos((prev) => [...prev, mapped]);
        return mapped;
      } catch (err) {
        console.error('[useClients] addDemo exception:', err);
        const stub: Demo = { ...demo, id: crypto.randomUUID() };
        return stub;
      }
    },
    [],
  );

  const updateDemo = useCallback(
    async (id: string, updates: Partial<Demo>) => {
      try {
        const row: Record<string, any> = {};
        if (updates.clientId !== undefined) row.client_id = updates.clientId;
        if (updates.contactName !== undefined) row.prospect_name = updates.contactName;
        if (updates.dateTime !== undefined) {
          const [datePart, timePart] = updates.dateTime.split('T');
          row.demo_date = datePart;
          if (timePart) row.demo_time = timePart;
        }
        if (updates.closerId !== undefined) row.closer_id = updates.closerId;
        if (updates.status !== undefined) row.status = updates.status;
        if (updates.outcome !== undefined) row.outcome = updates.outcome;
        if (updates.dealValue !== undefined) row.deal_value = updates.dealValue;
        if (updates.source !== undefined) row.source = updates.source;

        const { error } = await supabase.from('demos').update(row).eq('id', id);

        if (error) {
          console.error('[useClients] updateDemo error:', error);
        }

        setDemos((prev) => prev.map((d) => (d.id === id ? { ...d, ...updates } : d)));
      } catch (err) {
        console.error('[useClients] updateDemo exception:', err);
        setDemos((prev) => prev.map((d) => (d.id === id ? { ...d, ...updates } : d)));
      }
    },
    [],
  );

  // ----- Removal requests -----

  const submitRemovalRequest = useCallback(
    async (request: Omit<CloserRemovalRequest, 'id' | 'status'>) => {
      try {
        const { data, error } = await supabase
          .from('closer_requests')
          .insert({
            client_id: request.clientId,
            closer_id: request.closerId || null,
            request_type: request.requestType,
            reason: request.reason ?? null,
            status: 'pending',
          })
          .select('*, clients(company_name), closers(name)')
          .single();

        if (error) {
          console.error('[useClients] submitRemovalRequest error:', error);
          const stub: CloserRemovalRequest = { ...request, id: crypto.randomUUID(), status: 'pending' };
          return stub;
        }

        const mapped = mapRemovalRequestRow(data);
        setRemovalRequests((prev) => [...prev, mapped]);
        return mapped;
      } catch (err) {
        console.error('[useClients] submitRemovalRequest exception:', err);
        const stub: CloserRemovalRequest = { ...request, id: crypto.randomUUID(), status: 'pending' };
        return stub;
      }
    },
    [],
  );

  const updateRemovalRequest = useCallback(
    async (id: string, status: 'approved' | 'dismissed') => {
      try {
        const { error } = await supabase
          .from('closer_requests')
          .update({ status, processed_at: new Date().toISOString() })
          .eq('id', id);

        if (error) {
          console.error('[useClients] updateRemovalRequest error:', error);
        }

        setRemovalRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      } catch (err) {
        console.error('[useClients] updateRemovalRequest exception:', err);
        setRemovalRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      }
    },
    [],
  );

  // ----- Pipeline CRUD -----

  const addPipelineItem = useCallback(
    async (item: Omit<PipelineItem, 'id'>) => {
      try {
        const stageFromStatus: Record<string, string> = {
          'pre-onboard': 'lead',
          active: 'demo_showed',
          closed: 'closed',
        };

        const { data, error } = await supabase
          .from('funnel_events')
          .insert({
            client_id: '00000000-0000-0000-0000-000000000000', // placeholder; pipeline items may not map 1-1
            ghl_contact_id: 'manual-pipeline-' + crypto.randomUUID(),
            stage: stageFromStatus[item.status] ?? 'lead',
            deal_value_cents: Math.round((item.value ?? 0) * 100),
            closer_name: item.clientName,
          })
          .select()
          .single();

        if (error) {
          console.error('[useClients] addPipelineItem error:', error);
          const stub: PipelineItem = { ...item, id: crypto.randomUUID() };
          return stub;
        }

        const mapped = mapPipelineRow(data);
        setPipeline((prev) => [...prev, mapped]);
        return mapped;
      } catch (err) {
        console.error('[useClients] addPipelineItem exception:', err);
        const stub: PipelineItem = { ...item, id: crypto.randomUUID() };
        return stub;
      }
    },
    [],
  );

  const updatePipelineItem = useCallback(
    async (id: string, updates: Partial<PipelineItem>) => {
      try {
        const row: Record<string, any> = {};
        if (updates.clientName !== undefined) row.closer_name = updates.clientName;
        if (updates.value !== undefined) row.deal_value_cents = Math.round(updates.value * 100);
        if (updates.status !== undefined) {
          const stageMap: Record<string, string> = {
            'pre-onboard': 'lead',
            active: 'demo_showed',
            closed: 'closed',
          };
          row.stage = stageMap[updates.status] ?? updates.status;
        }

        const { error } = await supabase.from('funnel_events').update(row).eq('id', id);

        if (error) {
          console.error('[useClients] updatePipelineItem error:', error);
        }

        setPipeline((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)));
      } catch (err) {
        console.error('[useClients] updatePipelineItem exception:', err);
        setPipeline((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)));
      }
    },
    [],
  );

  const deletePipelineItem = useCallback(
    async (id: string) => {
      try {
        const { error } = await supabase.from('funnel_events').delete().eq('id', id);

        if (error) {
          console.error('[useClients] deletePipelineItem error:', error);
        }

        setPipeline((prev) => prev.filter((p) => p.id !== id));
      } catch (err) {
        console.error('[useClients] deletePipelineItem exception:', err);
        setPipeline((prev) => prev.filter((p) => p.id !== id));
      }
    },
    [],
  );

  // ----- Stats (computed from local state -- same logic as before) -----

  const getClientStats = useCallback(() => {
    const active = clients.filter((c) => c.status === 'active').length;
    const elite = clients.filter((c) => c.package === 'elite' && c.status === 'active').length;
    const standard = clients.filter((c) => c.package === 'standard' && c.status === 'active').length;
    const churned = clients.filter((c) => c.status === 'churned').length;
    return { total: clients.length, active, elite, standard, churned };
  }, [clients]);

  const getPendingRequestsCount = useCallback(() => {
    return removalRequests.filter((r) => r.status === 'pending').length;
  }, [removalRequests]);

  // ----- Return (same shape as before + loading) -----

  return {
    clients,
    demos,
    removalRequests,
    pipeline,
    tickets,
    upgradeRequests,
    teamMembers,
    loading,
    addClient,
    updateClient,
    pauseClient,
    unpauseClient,
    deactivateClient,
    deleteClient,
    addDemo,
    updateDemo,
    submitRemovalRequest,
    updateRemovalRequest,
    addPipelineItem,
    updatePipelineItem,
    deletePipelineItem,
    getClientStats,
    getPendingRequestsCount,
  };
}
